/* ============================================================
   The record contract.

   Seven record shapes across two object stores, written by seven tools,
   each stamping its own id and date and — in one case — forgetting to
   stamp its kind at all, which is how a saved emissions assessment came
   to display "$0.000 per tonne-km". Audit findings C-3 and M-1 are the
   same finding seen from two directions: nothing owned the shape of a
   stored record, so every tool improvised one.

   This module owns it. A tool says what kind of thing it is saving and
   hands over the payload; id, kind, schema version and timestamp are
   stamped here. A tool cannot forget a field it never sets.

   WHY VERSION AT ALL. domains.js is a living model. When a domain
   weight changes, a scorecard saved last quarter is not wrong — it is
   an answer to a slightly different question, and rendering it through
   today's logic without knowing that is how a product quietly starts
   lying about its own history. The version is what lets `migrate` tell
   the difference, and what lets a future reader refuse to guess.

   IMPORT IS THE OTHER HALF. The privacy notice tells people to export
   what matters because nothing can be recovered for them. An export
   with no import is a one-way door dressed up as an escape hatch, so
   there is an importer, and it treats the file as hostile: every record
   is validated, unknown kinds are refused rather than rendered, and the
   `kind` lookup goes through an own-property check because
   `{"kind":"constructor"}` in a hand-edited file otherwise returns a
   truthy function and walks straight past the fallback. That is audit
   A-02 in a new place — the same bug the brand switcher had.
   ============================================================ */

import { idb, newId } from './storage.js';

/**
 * Bumped when the meaning of a stored field changes — not when a field
 * is added. Readers must tolerate missing fields; that is what makes
 * additive change cheap and this number meaningful.
 */
export const SCHEMA_VERSION = 2;

/** Records written before versioning existed. */
export const UNVERSIONED = 1;

export const STORES = ['scorecards', 'benchmarks', 'barriers'];

/**
 * Every kind that may legitimately appear in a store, and which store
 * it belongs in. An import naming anything else is refused: the point
 * of the list is that it is exhaustive.
 */
export const KNOWN_KINDS = {
  scorecard: 'scorecards',
  benchmark: 'benchmarks',
  'landed-cost': 'benchmarks',
  profitability: 'benchmarks',
  emissions: 'benchmarks',
  financeability: 'benchmarks',
  'corridor-run': 'benchmarks',
  capital: 'benchmarks',
  barrier: 'barriers'
};

/** Own-property lookup. See the A-02 note above. */
export function isKnownKind(kind) {
  return typeof kind === 'string' && Object.prototype.hasOwnProperty.call(KNOWN_KINDS, kind);
}

export function storeFor(kind) {
  return isKnownKind(kind) ? KNOWN_KINDS[kind] : null;
}

/* ============================================================
   Writing
   ============================================================ */

/**
 * Stamp and persist. Never throws — a failed write must not take the
 * on-screen result with it (audit A-06).
 *
 * @param {string} kind    a key of KNOWN_KINDS
 * @param {object} payload the tool's own fields
 * @returns {Promise<object|null>} the stamped record, or null if refused
 */
export async function saveRecord(kind, payload, now = new Date()) {
  const store = storeFor(kind);
  if (!store) {
    console.warn(`Refusing to save unknown record kind "${kind}"`);
    return null;
  }

  const record = {
    ...payload,
    id: payload?.id || newId(),
    kind,
    schemaVersion: SCHEMA_VERSION,
    date: payload?.date || now.toISOString()
  };

  await idb.putQuiet(store, record);
  return record;
}

/* ============================================================
   Reading
   ============================================================ */

/**
 * Bring a record up to the current schema.
 *
 * Version 1 → 2 is the interesting one and it is entirely inference:
 * v1 records carry no `schemaVersion` and, in the benchmarks store, may
 * carry no `kind` either. A kindless record with a costPerTonKm is a
 * corridor cost analysis, because for a long time that tool was the only
 * thing writing there. Anything else kindless is left alone and reported
 * as unrecognised rather than assigned a plausible identity — guessing
 * is what produced the fabricated "$0.000 per tonne-km" in the first
 * place.
 */
export function migrate(record, store) {
  if (!record || typeof record !== 'object') return null;

  const version = Number.isFinite(record.schemaVersion) ? record.schemaVersion : UNVERSIONED;
  if (version >= SCHEMA_VERSION) return record;

  const upgraded = { ...record, schemaVersion: SCHEMA_VERSION };

  if (!upgraded.kind) {
    if (store === 'scorecards') upgraded.kind = 'scorecard';
    else if (store === 'barriers') upgraded.kind = 'barrier';
    else if (store === 'benchmarks' && Number.isFinite(record.costPerTonKm)) {
      upgraded.kind = 'benchmark';
    }
    // else: deliberately left kindless. The reader shows it as an
    // unrecognised record; it does not borrow someone else's label.
  }

  upgraded.migratedFrom = version;
  return upgraded;
}

/** Read a store, migrating on the way out. Never throws. */
export async function readRecords(store) {
  try {
    const rows = await idb.getAll(store);
    return rows.map((r) => migrate(r, store)).filter(Boolean);
  } catch (error) {
    console.warn(`Could not read "${store}":`, error);
    return null; // null means unavailable, [] means genuinely empty
  }
}

/* ============================================================
   Export / import
   ============================================================ */

export const EXPORT_FORMAT = 'kanda.export.v2';

export function buildExport(payload, now = new Date()) {
  return {
    format: EXPORT_FORMAT,
    schemaVersion: SCHEMA_VERSION,
    exportedAt: now.toISOString(),
    application: 'Kanda Logistics Advisory',
    ...payload
  };
}

/**
 * Validate a parsed export file without touching storage.
 *
 * Separate from the write so the caller can show the user exactly what
 * is about to happen before it happens. Restoring a backup over live
 * work is not something to discover after the fact.
 *
 * @returns {{ok: boolean, error?: string, records: Array, rejected: Array}}
 */
export function inspectImport(parsed) {
  const rejected = [];
  const records = [];

  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
    return { ok: false, error: 'That file is not a Kanda export.', records, rejected };
  }
  if (parsed.format !== EXPORT_FORMAT && !Array.isArray(parsed.scorecards)) {
    return {
      ok: false,
      error: 'That file is not a Kanda export, or it came from a much older version.',
      records,
      rejected
    };
  }

  // Both the current shape and the first export shape, which had no
  // `format` and used `calculations` for the benchmarks store.
  const groups = [
    { rows: parsed.scorecards, store: 'scorecards' },
    { rows: parsed.calculations, store: 'benchmarks' },
    { rows: parsed.benchmarks, store: 'benchmarks' },
    { rows: parsed.barriers, store: 'barriers' }
  ];

  for (const { rows, store } of groups) {
    if (!Array.isArray(rows)) continue;

    for (const row of rows) {
      if (!row || typeof row !== 'object' || Array.isArray(row)) {
        rejected.push({ reason: 'not an object', row: null });
        continue;
      }

      const migrated = migrate(row, store);

      if (!migrated.kind) {
        rejected.push({ reason: 'no recognisable kind', id: migrated.id });
        continue;
      }
      if (!isKnownKind(migrated.kind)) {
        // Covers "constructor", "__proto__" and anything simply unknown.
        rejected.push({ reason: `unknown kind "${String(migrated.kind)}"`, id: migrated.id });
        continue;
      }
      if (storeFor(migrated.kind) !== store) {
        rejected.push({ reason: `kind "${migrated.kind}" does not belong in ${store}`, id: migrated.id });
        continue;
      }
      if (typeof migrated.id !== 'string' || migrated.id === '') {
        rejected.push({ reason: 'missing id', id: null });
        continue;
      }
      if (typeof migrated.date !== 'string' || Number.isNaN(new Date(migrated.date).getTime())) {
        rejected.push({ reason: 'missing or unreadable date', id: migrated.id });
        continue;
      }

      records.push({ store, record: migrated });
    }
  }

  if (records.length === 0 && rejected.length === 0) {
    return { ok: false, error: 'That export contains nothing to restore.', records, rejected };
  }

  return { ok: true, records, rejected };
}

/**
 * Write inspected records. Merges by id rather than replacing the store:
 * importing a backup should not silently destroy work done since it was
 * taken. A record already present is overwritten by the imported one,
 * which is what "restore this backup" means.
 */
export async function applyImport(inspected) {
  let written = 0;
  let failed = 0;

  for (const { store, record } of inspected.records) {
    const ok = await idb.putQuiet(store, record);
    if (ok) written += 1;
    else failed += 1;
  }

  return { written, failed, rejected: inspected.rejected.length };
}
